import React from 'react';
import { Play } from 'lucide-react';

/*
 * WebsiteBlockPreview — a tiny (~64px tall) LIVE visual hint for each Tech
 * Settings "Homepage Feeds" card. It renders a schematic thumbnail from the
 * SAME `settings[key]` object the editor saves, so it is NEVER stale — when the
 * operator edits a block, the preview reflects it immediately (no screenshot to
 * go out of date). Purely decorative (pointer-events-none).
 *
 * NOTE (option B, kept for later): if a pixel-accurate snapshot of the live WP
 * block is ever wanted, swap this component for an <img> whose src is a
 * thumbnail regenerated on each OL→WP publish.
 */

const ORANGE = '#e87722';
const wrap = 'relative w-full h-16 rounded-md overflow-hidden border border-slate-700/70 pointer-events-none select-none';

const Box = ({ style, className = '', children }) => (
  <div className={`w-full h-full ${className}`} style={style}>{children}</div>
);

function Ticker({ d }) {
  const bg = d.ticker_bg || '#0d0d1a';
  const accent = d.win_color || ORANGE;
  const fc = d.font_color || '#fff';
  return (
    <Box style={{ background: bg }} className="flex items-center gap-1.5 px-2 overflow-hidden">
      {['SAB', 'OPP', 'SAB', 'RIV'].map((t, i) => (
        <span key={i} className="flex items-center gap-1 text-[8px] font-semibold whitespace-nowrap" style={{ color: fc }}>
          <span>{t}</span>
          <span style={{ color: i % 2 ? fc : accent }}>{i % 2 ? '1' : '3'}</span>
          <span style={{ color: '#3a3a4d' }}>•</span>
        </span>
      ))}
    </Box>
  );
}

function Hero({ d }) {
  const s = (d.slides && d.slides[0]) || {};
  return (
    <Box style={{ background: `linear-gradient(120deg, ${ORANGE}22, #0d0d1a)` }} className="relative flex items-end p-2">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,#e8772233,transparent)]" />
      <div className="relative">
        <div className="text-[9px] font-bold text-white leading-none">{s.title || 'SOUTH WAKE SABRES'}</div>
        <div className="text-[7px] text-slate-300">{s.subtitle || `${d.slide_count || 3} slides`}</div>
      </div>
    </Box>
  );
}

function Sponsors({ d }) {
  const tag = (d.taglines && d.taglines[0]) || 'Proud sponsor of the Sabres';
  return (
    <Box style={{ background: '#12121f' }} className="flex flex-col items-center justify-center gap-1">
      <div className="flex gap-1.5">
        {[0, 1, 2, 3].map(i => <div key={i} className="w-7 h-4 rounded-sm bg-slate-600/70" />)}
      </div>
      <div className="text-[7px] text-slate-400 italic truncate max-w-[90%]">{tag}</div>
    </Box>
  );
}

function SponsorPackages({ d }) {
  const n = (d.packages && d.packages.length) || 3;
  return (
    <Box style={{ background: '#12121f' }} className="flex items-center justify-center gap-1.5 px-2">
      {Array.from({ length: Math.min(n, 3) }).map((_, i) => (
        <div key={i} className="flex-1 h-11 rounded-sm border border-slate-600 flex flex-col items-center justify-center">
          <div className="w-6 h-1 rounded" style={{ background: ORANGE }} />
          <div className="text-[7px] text-slate-300 mt-1">${(i + 1) * 5}00</div>
        </div>
      ))}
    </Box>
  );
}

function UpcomingGames({ d }) {
  const n = Math.min(d.cards_per_view || 4, 4);
  return (
    <Box style={{ background: '#0f1120' }} className="flex items-center gap-1 px-2">
      {Array.from({ length: n }).map((_, i) => (
        <div key={i} className="flex-1 h-11 rounded-sm bg-slate-800 border border-slate-700 flex flex-col justify-center items-center">
          <div className="text-[7px]" style={{ color: ORANGE }}>{['BK', 'BB', 'VB'][i % 3]}</div>
          <div className="text-[7px] text-slate-400">vs OPP</div>
        </div>
      ))}
    </Box>
  );
}

function HomeSections({ d }) {
  const cols = ['Stories', 'Sports', 'Champs'];
  return (
    <Box style={{ background: '#0f1120' }} className="flex items-center gap-1 px-2">
      {cols.map((c, i) => (
        <div key={i} className="flex-1 h-11 rounded-sm bg-slate-800 border border-slate-700 flex items-center justify-center">
          <span className="text-[7px] text-slate-300">{c}</span>
        </div>
      ))}
    </Box>
  );
}

function Stats({ d }) {
  const row = Array.isArray(d) ? (d[0] || {}) : {};
  const cells = [
    [row.value_total ?? 128, row.label_total || 'Wins'],
    [row.value_alltime ?? 6, 'Titles'],
    [row.value_years ?? 12, 'Years'],
    [row.value_active ?? 8, 'Teams'],
  ];
  return (
    <Box style={{ background: '#0d0d1a' }} className="flex items-center justify-around px-1">
      {cells.map(([v, l], i) => (
        <div key={i} className="text-center">
          <div className="text-[11px] font-extrabold leading-none" style={{ color: row.font_color || ORANGE }}>{v}</div>
          <div className="text-[6px] text-slate-400 uppercase">{l}</div>
        </div>
      ))}
    </Box>
  );
}

function SabresTv({ d }) {
  return (
    <Box style={{ background: '#140b0b' }} className="flex flex-col justify-center px-2 gap-1">
      <div className="text-[8px] font-bold text-red-400">{d.section_title || 'Sabres TV'}</div>
      <div className="flex gap-1">
        {Array.from({ length: Math.min(d.video_count || 3, 4) }).map((_, i) => (
          <div key={i} className="flex-1 h-6 rounded-sm bg-slate-800 flex items-center justify-center">
            <Play className="h-2.5 w-2.5 text-red-400" />
          </div>
        ))}
      </div>
    </Box>
  );
}

function Nav({ d }) {
  return (
    <Box style={{ background: '#0d0d1a' }} className="flex items-center justify-between px-2">
      <div className="w-8 h-6 rounded-sm bg-slate-600 flex items-center justify-center text-[6px] text-slate-300">LOGO</div>
      <div className="flex gap-1.5">
        {['HOME', 'TEAMS', 'NEWS'].map(t => <span key={t} className="text-[7px] text-slate-300">{t}</span>)}
        <span className="text-[7px] px-1 rounded" style={{ background: ORANGE, color: '#fff' }}>REGISTER</span>
      </div>
    </Box>
  );
}

function Footer({ d }) {
  const bg = d.bg_color || '#0d0d1a';
  const tc = d.text_color || '#cbd5e1';
  const acc = d.accent_color || ORANGE;
  const links = (d.quick_links || []).slice(0, 4);
  return (
    <Box style={{ background: bg }} className="flex flex-col justify-center px-2 gap-1">
      <div className="text-[7px] font-semibold" style={{ color: acc }}>{d.tagline || 'Building Champions'}</div>
      <div className="flex gap-2 flex-wrap">
        {(links.length ? links : [{ label: 'Register' }, { label: 'Schedule' }, { label: 'Contact' }]).map((l, i) => (
          <span key={i} className="text-[6px]" style={{ color: tc }}>{l.label}</span>
        ))}
      </div>
      <div className="flex gap-1">{['f', 'in', '▶'].map((s, i) => <span key={i} className="text-[6px] w-2.5 h-2.5 rounded-full flex items-center justify-center" style={{ background: '#ffffff22', color: tc }}>{s}</span>)}</div>
    </Box>
  );
}

function RegisterCta({ d }) {
  return (
    <Box style={{ background: '#0f1120' }} className="flex items-center justify-center">
      <span className="text-[9px] font-bold px-3 py-1 rounded-full" style={{ background: d.enabled ? ORANGE : '#475569', color: '#fff' }}>
        {(d.label || 'Register').toUpperCase()}
      </span>
    </Box>
  );
}

function ListRows({ d, title, rows = 3, bg = '#0f1120' }) {
  return (
    <Box style={{ background: bg }} className="flex flex-col justify-center px-2 gap-1">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <div className="w-3.5 h-3.5 rounded-full bg-slate-600" />
          <div className="h-1.5 rounded bg-slate-600" style={{ width: `${60 - i * 12}%` }} />
        </div>
      ))}
    </Box>
  );
}

function Doc({ title }) {
  return (
    <Box style={{ background: '#111827' }} className="flex flex-col justify-center px-2 gap-1">
      <div className="text-[8px] font-bold text-slate-200">{title}</div>
      <div className="h-1.5 w-4/5 rounded bg-slate-600" />
      <div className="h-1.5 w-3/5 rounded bg-slate-700" />
    </Box>
  );
}

function Donation({ d }) {
  return (
    <Box style={{ background: '#0f1120' }} className="flex items-center justify-center gap-2">
      <div className="w-9 h-9 bg-white rounded-sm grid grid-cols-3 grid-rows-3 gap-0.5 p-0.5">
        {Array.from({ length: 9 }).map((_, i) => <div key={i} className={i % 2 ? 'bg-white' : 'bg-black'} />)}
      </div>
      <span className="text-[8px] font-semibold px-2 py-1 rounded" style={{ background: ORANGE, color: '#fff' }}>{d.button_label || 'Donate'}</span>
    </Box>
  );
}

function Cards({ n = 6 }) {
  return (
    <Box style={{ background: '#0f1120' }} className="grid grid-cols-3 gap-1 p-1.5">
      {Array.from({ length: n }).map((_, i) => <div key={i} className="rounded-sm bg-slate-800 border border-slate-700" />)}
    </Box>
  );
}

function Standings() {
  return (
    <Box style={{ background: '#0f1120' }} className="flex flex-col justify-center px-2 gap-0.5">
      {[0, 1, 2].map(i => (
        <div key={i} className="flex justify-between">
          <div className="h-1.5 w-2/5 rounded bg-slate-600" />
          <div className="h-1.5 w-8 rounded" style={{ background: i === 0 ? ORANGE : '#475569' }} />
        </div>
      ))}
    </Box>
  );
}

const RENDERERS = {
  ticker: (d) => <Ticker d={d} />,
  hero: (d) => <Hero d={d} />,
  sponsors: (d) => <Sponsors d={d} />,
  sponsor_packages: (d) => <SponsorPackages d={d} />,
  upcoming_games: (d) => <UpcomingGames d={d} />,
  home_sections: (d) => <HomeSections d={d} />,
  stats: (d) => <Stats d={d} />,
  sabres_tv: (d) => <SabresTv d={d} />,
  nav: (d) => <Nav d={d} />,
  footer: (d) => <Footer d={d} />,
  register_cta: (d) => <RegisterCta d={d} />,
  leadership: () => <ListRows title="Leadership" rows={3} />,
  about_page: () => <Doc title="About the Sabres" />,
  members_page: () => <Cards n={6} />,
  donation: (d) => <Donation d={d} />,
  standings: () => <Standings />,
  permissions: () => <ListRows title="Permissions" rows={3} bg="#131a2b" />,
};

export const WebsiteBlockPreview = ({ kind, data }) => {
  const render = RENDERERS[kind];
  if (!render) return null;
  return (
    <div className={wrap} data-testid={`website-preview-${kind}`} aria-hidden="true">
      {render(data || {})}
    </div>
  );
};

export default WebsiteBlockPreview;
